/**
 * SOLID Review
 * S — Strong. One rule: how a REST call's answer becomes a port's result — the server's refusal in its own
 *     words, anything else as a server that could not be asked.
 * O — Good. A new failure kind is one branch here, for every REST gateway at once.
 * L — N/A — functions.
 * I — Strong. Two exports.
 * D — Strong. The REST client's error class and the Application's failure code.
 */
import { SERVER_REFUSED } from '../../application/ports/contracts';
import { RestApiError } from './clients';

interface IEntityRestFailure {
  readonly code: string;
  readonly message: string;
}

export type TRestResult<T> = { readonly success: true; readonly value: T } | { readonly success: false; readonly error: IEntityRestFailure };

const UNREACHABLE: IEntityRestFailure = { code: 'UNAVAILABLE', message: 'Could not reach the server.' };

/**
 * A REST call through a port (frontend strict-ports plan, FE3; shared since FE4a). A refusal the server
 * ANSWERED (`RestApiError`) fails as `SERVER` with its words, fit to show a player; anything else — a fetch
 * that failed, a body the contract refused — is `UNAVAILABLE`, a diagnostic. The REST adapters threw; a
 * port answers.
 */
export async function restCall<T>(call: () => Promise<T>): Promise<TRestResult<T>> {
  try {
    return { success: true, value: await call() };
  } catch (error) {
    return restFailure(error);
  }
}

export function restFailure<T>(error: unknown): TRestResult<T> {
  if (error instanceof RestApiError) {
    const refused: IEntityRestFailure = { code: SERVER_REFUSED, message: error.message };
    return { success: false, error: refused };
  }
  return { success: false, error: UNREACHABLE };
}
